import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Stack,
  Chip,
} from '@mui/material';
import {
  Save,
  Edit,
  Business,
  Phone,
  Email,
  Language,
  Instagram,
  AccountBalance,
  CalendarToday,
  Percent,
  CheckCircle,
  Link as LinkIcon,
  Store,
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { useAuth } from '../../components/AuthProvider';

interface SellerProfile {
  id: string;
  user_id: string;
  store_name: string;
  store_description: string | null;
  business_number: string;
  business_type: string;
  owner_name: string;
  address: string;
  contact_email: string;
  contact_phone: string;
  bank_name: string;
  bank_account: string;
  bank_account_holder: string;
  status: string;
  joined_at: string;
  commission_rate: number;
  payment_fee_rate: number;
  profile_image: string | null;
  banner_image: string | null;
  bio: string | null;
  social_links: {
    instagram?: string;
    website?: string;
    tiktok?: string;
    youtube?: string;
  } | null;
}

const WORKERS_URL = 'https://share2dm-webhook.share2dm.workers.dev';

interface Brand {
  id: string;
  ig_username: string | null;
  ig_account_id: string | null;
  cafe24_mall_id: string | null;
  cafe24_connected_at: string | null;
}

export default function SellerProfile() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<SellerProfile | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [brand, setBrand] = useState<Brand | null>(null);
  const [mallId, setMallId] = useState('');
  const [cafe24Connecting, setCafe24Connecting] = useState(false);

  // 수정 가능한 필드
  const [formData, setFormData] = useState({
    store_description: '',
    bio: '',
    address: '',
    contact_phone: '',
    contact_email: '',
    instagram: '',
    website: '',
    tiktok: '',
    youtube: '',
  });

  useEffect(() => {
    loadProfile();
  }, [user]);

  const loadProfile = async () => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    try {
      setLoading(true);

      // Seller Profile 조회
      const { data: profileData, error: profileError } = await supabase
        .from('seller_profile')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (profileError) {
        console.error('Error loading profile:', profileError);
        return;
      }

      setProfile(profileData);

      // share2dm_brands 조회
      const { data: brandData } = await supabase
        .from('share2dm_brands')
        .select('id, ig_username, ig_account_id, cafe24_mall_id, cafe24_connected_at')
        .eq('clozet_seller_id', profileData.id)
        .maybeSingle();
      setBrand(brandData as Brand | null);

      // 폼 데이터 초기화
      setFormData({
        store_description: profileData.store_description || '',
        bio: profileData.bio || '',
        address: profileData.address || '',
        contact_phone: profileData.contact_phone || '',
        contact_email: profileData.contact_email || '',
        instagram: profileData.social_links?.instagram || '',
        website: profileData.social_links?.website || '',
        tiktok: profileData.social_links?.tiktok || '',
        youtube: profileData.social_links?.youtube || '',
      });

    } catch (error) {
      console.error('Error in loadProfile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!profile) return;

    try {
      setSaving(true);

      // social_links JSON 생성
      const socialLinks: any = {};
      if (formData.instagram) socialLinks.instagram = formData.instagram;
      if (formData.website) socialLinks.website = formData.website;
      if (formData.tiktok) socialLinks.tiktok = formData.tiktok;
      if (formData.youtube) socialLinks.youtube = formData.youtube;

      // Seller Profile 업데이트
      const { error: profileError } = await supabase
        .from('seller_profile')
        .update({
          store_description: formData.store_description,
          bio: formData.bio,
          address: formData.address,
          contact_phone: formData.contact_phone,
          contact_email: formData.contact_email,
          social_links: socialLinks,
        })
        .eq('id', profile.id);

      if (profileError) {
        console.error('Error updating profile:', profileError);
        alert(t('seller.profile.saveError'));
        return;
      }

      alert(t('seller.profile.saveSuccess'));
      setEditMode(false);
      loadProfile(); // 새로고침

    } catch (error) {
      console.error('Error in handleSave:', error);
      alert(t('seller.profile.saveException'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    // 폼 데이터 리셋
    if (profile) {
      setFormData({
        store_description: profile.store_description || '',
        bio: profile.bio || '',
        address: profile.address || '',
        contact_phone: profile.contact_phone || '',
        contact_email: profile.contact_email || '',
        instagram: profile.social_links?.instagram || '',
        website: profile.social_links?.website || '',
        tiktok: profile.social_links?.tiktok || '',
        youtube: profile.social_links?.youtube || '',
      });
    }
  };

  const handleIgRelink = async () => {
    if (!user || !profile) return;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.provider_token) {
      alert('세션이 만료되었습니다. 로그아웃 후 다시 로그인해주세요.');
      return;
    }
    const res = await fetch(`${WORKERS_URL}/auth/init-from-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token: session.provider_token, clozet_seller_id: profile.id, notification_email: profile.contact_email }),
    });
    const data: any = await res.json();
    if (data.error) { alert(`연동 실패: ${data.error}`); return; }
    window.location.reload();
  };

  const handleCafe24Connect = async () => {
    if (!brand || !mallId.trim()) return;
    setCafe24Connecting(true);
    const url = `${WORKERS_URL}/auth/cafe24?brand_id=${brand.id}&mall_id=${encodeURIComponent(mallId.trim().toLowerCase())}`;
    window.location.href = url;
  };

  if (loading) {
    return (
      <Box sx={{ ...styles.pageContainer, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box sx={styles.pageContainer}>
        <Alert severity="error">{t('seller.profile.profileError')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={styles.pageContainer}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ ...styles.title, fontWeight: 'bold' }}>
          {t('seller.profile.title')}
        </Typography>
        {!editMode ? (
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => setEditMode(true)}
          >
            {t('seller.profile.editProfile')}
          </Button>
        ) : (
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={saving}
            >
              {t('seller.profile.cancelBtn')}
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? t('seller.profile.saving') : t('seller.profile.saveBtn')}
            </Button>
          </Stack>
        )}
      </Stack>

      {/* 연동 상태 */}
      <Paper sx={{ ...styles.paper, p: 2.5, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1.5} color={isDarkMode ? '#fff' : '#111'}>연동 상태</Typography>
        <Stack direction="row" spacing={3} flexWrap="wrap">
          {/* Instagram */}
          <Stack direction="row" spacing={1.5} alignItems="center" flex={1} minWidth={200}>
            <Instagram sx={{ color: '#E1306C', fontSize: 26 }} />
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={600} color={isDarkMode ? '#fff' : '#111'}>Instagram</Typography>
                {brand?.ig_account_id
                  ? <Chip label="연동됨" size="small" color="success" />
                  : <Chip label="미연동" size="small" color="default" />}
              </Stack>
              {brand?.ig_username && (
                <Typography variant="caption" color="text.secondary">@{brand.ig_username}</Typography>
              )}
            </Box>
            <Button size="small" variant="outlined" onClick={handleIgRelink}
              sx={{ borderColor: '#E1306C', color: '#E1306C', '&:hover': { borderColor: '#be185d', bgcolor: '#E1306C10' }, whiteSpace: 'nowrap' }}>
              {brand?.ig_account_id ? '재연동' : '연동하기'}
            </Button>
          </Stack>

          {/* Cafe24 */}
          <Stack direction="row" spacing={1.5} alignItems="center" flex={1} minWidth={200}>
            <Store sx={{ color: '#006DCC', fontSize: 26 }} />
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={600} color={isDarkMode ? '#fff' : '#111'}>Cafe24</Typography>
                {brand?.cafe24_mall_id
                  ? <Chip label="연동됨" size="small" color="success" />
                  : <Chip label="미연동" size="small" color="default" />}
              </Stack>
              {brand?.cafe24_mall_id
                ? <Typography variant="caption" color="text.secondary">{brand.cafe24_mall_id}.cafe24.com</Typography>
                : (
                  <Stack direction="row" spacing={0.5} mt={0.5}>
                    <TextField size="small" placeholder="쇼핑몰 ID" value={mallId}
                      onChange={e => setMallId(e.target.value)}
                      sx={{ width: 130, '& .MuiInputBase-input': { py: 0.5, fontSize: 12 } }} />
                    <Button size="small" variant="contained"
                      onClick={handleCafe24Connect}
                      disabled={cafe24Connecting || !mallId.trim()}
                      sx={{ bgcolor: '#006DCC', '&:hover': { bgcolor: '#0056a3' }, fontSize: 11, px: 1.5 }}>
                      {cafe24Connecting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : '연동'}
                    </Button>
                  </Stack>
                )}
            </Box>
          </Stack>
        </Stack>
      </Paper>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          • {t('seller.profile.readonlyNote')}
        </Typography>
      </Alert>

      {/* 기본 정보 (읽기 전용) */}
      <Paper sx={{ ...styles.paper, p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ ...styles.text, fontWeight: 'bold', mb: 2 }}>
          {t('seller.profile.basicInfo')}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Business color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={styles.text}>
                  {t('seller.profile.storeName')}
                </Typography>
                <Typography variant="body1" sx={styles.text}>
                  {profile.store_name}
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Business color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={styles.text}>
                  {t('seller.profile.businessNumber')}
                </Typography>
                <Typography variant="body1" sx={styles.text}>
                  {profile.business_number}
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Business color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={styles.text}>
                  {t('seller.profile.businessType')}
                </Typography>
                <Typography variant="body1" sx={styles.text}>
                  {profile.business_type === 'individual' ? t('seller.profile.individual') : t('seller.profile.corporate')}
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Business color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={styles.text}>
                  {t('seller.profile.ownerName')}
                </Typography>
                <Typography variant="body1" sx={styles.text}>
                  {profile.owner_name}
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AccountBalance color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={styles.text}>
                  {t('seller.profile.settlementBank')}
                </Typography>
                <Typography variant="body1" sx={styles.text}>
                  {profile.bank_name}
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AccountBalance color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={styles.text}>
                  {t('seller.profile.accountNumber')}
                </Typography>
                <Typography variant="body1" sx={styles.text}>
                  {profile.bank_account} ({profile.bank_account_holder})
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircle color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={styles.text}>
                  {t('seller.profile.accountStatus')}
                </Typography>
                <Typography variant="body1" sx={styles.text}>
                  {profile.status === 'active' ? t('seller.profile.statusActive') :
                   profile.status === 'pending' ? t('seller.profile.statusPending') :
                   profile.status === 'suspended' ? t('seller.profile.statusSuspended') : t('seller.profile.statusRejected')}
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarToday color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={styles.text}>
                  {t('seller.profile.joinDate')}
                </Typography>
                <Typography variant="body1" sx={styles.text}>
                  {new Date(profile.joined_at).toLocaleDateString('ko-KR')}
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Percent color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={styles.text}>
                  {t('seller.profile.platformFeeRate')}
                </Typography>
                <Typography variant="body1" sx={styles.text}>
                  {profile.commission_rate}%
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Percent color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={styles.text}>
                  {t('seller.profile.paymentFeeRate')}
                </Typography>
                <Typography variant="body1" sx={styles.text}>
                  {profile.payment_fee_rate}%
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* 스토어 및 프로필 정보 */}
      <Paper sx={{ ...styles.paper, p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ ...styles.text, fontWeight: 'bold', mb: 2 }}>
          {t('seller.profile.storeAndProfile')}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('seller.profile.storeDescription')}
              placeholder={t('seller.profile.storeDescriptionPlaceholder')}
              multiline
              rows={3}
              value={formData.store_description}
              onChange={(e) => setFormData({ ...formData, store_description: e.target.value })}
              disabled={!editMode}
              sx={styles.input}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('seller.profile.bio')}
              placeholder={t('seller.profile.bioPlaceholder')}
              multiline
              rows={4}
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              disabled={!editMode}
              sx={styles.input}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* 연락처 정보 */}
      <Paper sx={{ ...styles.paper, p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ ...styles.text, fontWeight: 'bold', mb: 2 }}>
          {t('seller.profile.contactInfo')}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('seller.profile.companyAddress')}
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              disabled={!editMode}
              InputProps={{
                startAdornment: <Business sx={{ mr: 1 }} color="action" />,
              }}
              sx={styles.input}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('seller.profile.contactPhone')}
              value={formData.contact_phone}
              onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              disabled={!editMode}
              InputProps={{
                startAdornment: <Phone sx={{ mr: 1 }} color="action" />,
              }}
              sx={styles.input}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('seller.profile.contactEmail')}
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              disabled={!editMode}
              InputProps={{
                startAdornment: <Email sx={{ mr: 1 }} color="action" />,
              }}
              sx={styles.input}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* 소셜 링크 */}
      <Paper sx={{ ...styles.paper, p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ ...styles.text, fontWeight: 'bold', mb: 2 }}>
          {t('seller.profile.socialLinks')}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('seller.profile.website')}
              placeholder="https://example.com"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              disabled={!editMode}
              InputProps={{
                startAdornment: <Language sx={{ mr: 1 }} color="action" />,
              }}
              sx={styles.input}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('seller.profile.instagram')}
              placeholder="https://instagram.com/username"
              value={formData.instagram}
              onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
              disabled={!editMode}
              InputProps={{
                startAdornment: <Instagram sx={{ mr: 1 }} color="action" />,
              }}
              sx={styles.input}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="TikTok"
              placeholder="https://tiktok.com/@username"
              value={formData.tiktok}
              onChange={(e) => setFormData({ ...formData, tiktok: e.target.value })}
              disabled={!editMode}
              InputProps={{
                startAdornment: <Language sx={{ mr: 1 }} color="action" />,
              }}
              sx={styles.input}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="YouTube"
              placeholder="https://youtube.com/@username"
              value={formData.youtube}
              onChange={(e) => setFormData({ ...formData, youtube: e.target.value })}
              disabled={!editMode}
              InputProps={{
                startAdornment: <Language sx={{ mr: 1 }} color="action" />,
              }}
              sx={styles.input}
            />
          </Grid>
        </Grid>
      </Paper>

    </Box>
  );
}
